import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useNotify } from '../context/NotifyContext';
import PasswordInput from './PasswordInput';
import GradientSpinner from './GradientSpinner';

/**
 * Display name + change password (authenticated). Uses refreshUser / onMessage like ProfilePhotoSection.
 */
export default function AccountSettingsSection({ user, refreshUser, onMessage }) {
  const { showSuccess, showError } = useNotify();
  const [name, setName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || '');
  }, [user?._id, user?.name]);

  const saveName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 120) {
      onMessage?.('Error: Enter a name (max 120 characters).');
      return;
    }
    setNameSaving(true);
    try {
      await authAPI.updateProfile({ name: trimmed });
      await refreshUser?.();
      showSuccess('Your name was updated.');
      onMessage?.('Your name was updated.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not update name.';
      showError(msg);
      onMessage?.(`Error: ${msg}`);
    } finally {
      setNameSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) {
      onMessage?.('Error: New password must be at least 6 characters.');
      return;
    }
    if (pw.next !== pw.confirm) {
      onMessage?.('Error: New password and confirmation do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await authAPI.changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      showSuccess('Password updated successfully.');
      onMessage?.('Password updated successfully.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not change password.';
      showError(msg);
      onMessage?.(`Error: ${msg}`);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <>
      <div className="card account-settings-card">
        <h2 className="card-title" style={{ marginBottom: '8px' }}>Display name</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          This name appears in your dashboard, messages, and listings.
        </p>
        <form onSubmit={saveName}>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label className="form-label" htmlFor="settings-display-name">
              Name
            </label>
            <input
              id="settings-display-name"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoComplete="name"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={nameSaving}>
            {nameSaving ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                <GradientSpinner size={18} />
                Saving…
              </span>
            ) : (
              'Save name'
            )}
          </button>
        </form>
      </div>

      <div className="card account-settings-card">
        <h2 className="card-title" style={{ marginBottom: '8px' }}>Change password</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Use a strong password you don’t use on other sites.
        </p>
        <form onSubmit={savePassword}>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label className="form-label" htmlFor="settings-current-password">
              Current password
            </label>
            <PasswordInput
              id="settings-current-password"
              value={pw.current}
              onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label className="form-label" htmlFor="settings-new-password">
              New password
            </label>
            <PasswordInput
              id="settings-new-password"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label className="form-label" htmlFor="settings-confirm-password">
              Confirm new password
            </label>
            <PasswordInput
              id="settings-confirm-password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwSaving}>
            {pwSaving ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                <GradientSpinner size={18} />
                Updating…
              </span>
            ) : (
              'Update password'
            )}
          </button>
        </form>
      </div>
    </>
  );
}
